﻿import axios from 'axios';
import * as Sentry from '@sentry/vue';
import currentUser from '../store/currentUser';

function reportError(err) {
  Sentry.captureException(err);
}

export default {
  debug: false,
  modules: {
    currentUser,
  },
  state: {
    isBrewLoading: true,
    brewsLoadError: false,
    brews: [],
    // When the brews in hand were requested, and whether the latest quiet
    // refresh failed. The dashboard says how fresh its numbers are from these.
    loadedAt: null,
    refreshFailed: false,
    // Bumped by every local change (finish, archive, delete). A refresh that
    // started before one answers with the older list, so it is dropped.
    revision: 0,
    // Deleted brews still inside their Undo window: hidden, not yet gone.
    hiddenIds: [],
  },

  // Actions report failures to Sentry and re-throw, so the calling
  // component can tell the brewer what happened and offer a retry.
  actions: {
    // quiet: a background refresh. It keeps the page as it is (no loading bar,
    // no error panel) and only notes a failure, so the page can say it's behind.
    async LOAD_BREWS({ commit, state }, { quiet = false } = {}) {
      if (!quiet) {
        commit('setIsBrewLoading', { isBrewLoading: true });
        commit('setBrewsLoadError', { brewsLoadError: false });
      }
      const revision = state.revision;
      const requestedAt = Date.now();

      try {
        const results = await axios.get('/api/users/dashboard/brews');

        if (quiet && state.revision !== revision) return;
        commit('setBrews', { brews: results.data || [], loadedAt: requestedAt });
      } catch (err) {
        if (quiet) {
          // Offline laptops and sleeping phones fail refreshes all the time;
          // that is a state to show, not an error to report.
          commit('setRefreshFailed', { refreshFailed: true });
          return;
        }
        reportError(err);
        commit('setBrewsLoadError', { brewsLoadError: true });
      } finally {
        if (!quiet) commit('setIsBrewLoading', { isBrewLoading: false });
      }
    },

    // Ends fermentation now. Resolves with the end date it replaced, for Undo.
    async FINISH_BREW({ commit }, { brew }) {
      let response;
      try {
        response = await axios.post(`/api/users/brews/${brew.brewId}/finish`);
      } catch (err) {
        reportError(err);
        throw err;
      }
      // The server stamps the end date. Group by this browser's clock, so a
      // clock a few seconds behind the server still reads the brew as finished.
      commit('SET_END_DATE', { brew, brewEndDate: new Date().toISOString() });
      return (response.data && response.data.previousBrewEndDate) || null;
    },

    async UNFINISH_BREW({ commit }, { brew, brewEndDate = null }) {
      try {
        await axios.post(`/api/users/brews/${brew.brewId}/unfinish`, { brewEndDate });
      } catch (err) {
        reportError(err);
        throw err;
      }
      commit('SET_END_DATE', { brew, brewEndDate });
    },

    async ARCHIVE_BREW({ commit }, { brew }) {
      try {
        await axios.post(`/api/users/brews/${brew.brewId}/archive`);
      } catch (err) {
        reportError(err);
        throw err;
      }
      commit('ARCHIVE_BREW', { brew });
    },

    async UNARCHIVE_BREW({ commit }, { brew }) {
      try {
        await axios.post(`/api/users/brews/${brew.brewId}/unarchive`);
      } catch (err) {
        reportError(err);
        throw err;
      }
      commit('UNARCHIVE_BREW', { brew });
    },

    async OPEN_BILLING_PORTAL() {
      try {
        const security = await axios.get('/application/settings/token');
        const response = await axios.post('/api/users/billing/portal-session', {}, {headers:{'X-CSRF-Token':security.data.token}});
        window.location = response.data.url;
      } catch (err) {
        reportError(err);
        throw err;
      }
    },

    async DELETE_BREW({ commit }, { brew }) {
      try {
        await axios.delete(`/api/users/brews/${brew.brewId}`);
      } catch (err) {
        reportError(err);
        throw err;
      }
      commit('DELETE_BREW', { brew });
    },
  },

  mutations: {
    setBrews: (state, { brews, loadedAt = Date.now() }) => {
      state.brews = brews;
      state.loadedAt = loadedAt;
      state.refreshFailed = false;
    },

    setRefreshFailed(state, { refreshFailed }) {
      state.refreshFailed = refreshFailed;
    },

    HIDE_BREW: (state, { brew }) => {
      if (state.hiddenIds.indexOf(brew.brewId) === -1) state.hiddenIds.push(brew.brewId);
    },

    UNHIDE_BREW: (state, { brew }) => {
      state.hiddenIds = state.hiddenIds.filter((id) => id !== brew.brewId);
    },

    SET_END_DATE: (state, { brew, brewEndDate }) => {
      state.revision++;
      const match = state.brews.find((p) => p.brewId === brew.brewId);

      if (match) match.brewEndDate = brewEndDate;
    },

    ARCHIVE_BREW: (state, { brew }) => {
      state.revision++;
      const match = state.brews.find((p) => p.brewId === brew.brewId);

      if (match) match.isArchived = true;
    },

    UNARCHIVE_BREW: (state, { brew }) => {
      state.revision++;
      const match = state.brews.find((p) => p.brewId === brew.brewId);

      if (match) match.isArchived = false;
    },

    DELETE_BREW: (state, { brew }) => {
      state.revision++;
      state.hiddenIds = state.hiddenIds.filter((id) => id !== brew.brewId);
      const idx = state.brews.map((p) => p.brewId).indexOf(brew.brewId);

      if (idx > -1) state.brews.splice(idx, 1);
    },
    setIsBrewLoading(state, { isBrewLoading }) {
      state.isBrewLoading = isBrewLoading;
    },
    setBrewsLoadError(state, { brewsLoadError }) {
      state.brewsLoadError = brewsLoadError;
    },
  },

  getters: {
    // Every brew the owner still has, less any deleted inside its Undo window.
    ownedBrews: (state) => {
      return state.hiddenIds.length ? state.brews.filter((brew) => state.hiddenIds.indexOf(brew.brewId) === -1) : state.brews;
    },
    archivedBrews: (state, getters) => {
      return getters.ownedBrews.filter((brew) => brew.isArchived);
    },
    brews: (state, getters) => {
      return getters.ownedBrews.filter((brew) => !brew.isArchived);
    },
  },
};
